/**
 * Anpassningsbart tilläggsavtal till bostadshyresavtal, IR twin of the
 * legacy `clean_tillagg_bostad/v1` template (ADR-0061).
 *
 * The body is the user's custom sections, anchored by default after
 * Avtalskoppling. The signature section — and anything anchored after it —
 * is present only when `include_signing` is on.
 */
import { applyDefaults, COMPOSITION_SCHEMA, COMPOSITION_VERSION } from '@hyranai/regta-document-ir';
import { CLEAN_TILLAGG_BOSTAD_BLOCKS, documentTitle } from '../clean_tillagg_bostad/blocks.js';
import { declaredSectionInstances } from '../common/custom-sections.js';
const SECTIONS = [
    { ref: 'hyresvard', blockRef: 'clean_tillagg_bostad.hyresvard@v1' },
    { ref: 'hyresgast', blockRef: 'clean_tillagg_bostad.hyresgast@v1', besidePrevious: true },
    { ref: 'avtalskoppling', blockRef: 'clean_tillagg_bostad.avtalskoppling@v1' },
    {
        ref: 'underskrift',
        blockRef: 'clean_tillagg_bostad.underskrifter@v1',
        includeWhen: { key: 'include_signing', equals: true },
        input: {
            statement: false,
            parties: [
                { label: 'Hyresvärd', role: 'lessor', ordinal: 0 },
                { label: 'Hyresgäst', role: 'lessee', ordinal: 0 },
                { label: 'Medhyresgäst', role: 'lessee', ordinal: 1, when_set: 'cotenant_name' },
            ],
        },
    },
];
/**
 * The legacy create-time defaults: the slots' own `default`s
 * (`document_title`, `include_signing`) and the `defaultCustomSections`
 * seed (one empty "Innehåll" section).
 */
export const CLEAN_TILLAGG_BOSTAD_DEFAULTS = {
    ...applyDefaults({ fields: CLEAN_TILLAGG_BOSTAD_BLOCKS.flatMap((block) => block.slots) }),
    __custom_sections: [
        { id: 'innehall', title: 'Innehåll', subSections: [{ id: 'innehall-1', components: [{ id: 'innehall-1-1', kind: 'text', body: '' }] }] },
    ],
};
export const cleanTillaggBostadV1 = (input = {}) => {
    const values = { ...CLEAN_TILLAGG_BOSTAD_DEFAULTS, ...input };
    return {
        schema: COMPOSITION_SCHEMA,
        version: COMPOSITION_VERSION,
        documentKey: 'clean_tillagg_bostad',
        documentVersion: 'v1',
        title: documentTitle(values),
        locale: 'sv-SE',
        meta: {
            category: 'Bostadshyra',
            numberStyle: 'paragraph',
            shortTitle: 'Anpassningsbart tilläggsavtal (bostad)',
            description: 'Ett tomt tilläggsavtal till ett bostadshyresavtal. Parter och fastighet ' +
                'ärvs från avtalet; resten skriver du själv som egna sektioner. Välj om ' +
                'tillägget ska signeras eller endast exporteras.',
            status: 'active',
            signingMode: 'digital',
            signingOptional: true,
            signingPlacementAnchors: 'per-party-label',
            connectedTo: ['bostadshyra', 'studentbostad'],
            connectedOnly: true,
            inheritFromParent: {
                parties: 'by-role-ordinal',
                property: true,
                values: [{ key: 'contract_number', fromParentKey: 'contract_number' }],
            },
            titleFromValueKey: 'document_title',
            defaultCustomSections: [{ title: 'Innehåll' }],
            partySlots: [
                { id: 'lessor', label: 'Hyresvärd', role: 'lessor', ordinal: 0, required: true, signs: true },
                { id: 'lessee_primary', label: 'Hyresgäst', role: 'lessee', ordinal: 0, required: true, signs: true },
                {
                    id: 'lessee_co',
                    label: 'Medhyresgäst',
                    role: 'lessee',
                    ordinal: 1,
                    signs: true,
                    help: 'Valfritt — lägg till om avtalet har två hyresgäster.',
                },
            ],
            propertySlots: [{ id: 'property', label: 'Fastighet', multi: false, required: true }],
        },
        values,
        blocks: [
            { instanceId: 'letterhead', blockRef: 'clean_tillagg_bostad.letterhead@v1', slot: 'letterhead', input: {} },
            { instanceId: 'footer', blockRef: 'common.page-footer@v1', slot: 'footer', input: {} },
            ...declaredSectionInstances(SECTIONS, { customBlockRef: 'clean_tillagg_bostad.custom-sections@v1' }),
        ],
    };
};
//# sourceMappingURL=clean_tillagg_bostad.js.map